import type { Battle } from "@loot-os/api-contract";
import { formatAmountWithUnit } from "@loot-os/api-contract";
import { BoxImage } from "@loot-os/ui/components/box-image";
import { Button, buttonVariants } from "@loot-os/ui/components/button";
import { Card, CardContent } from "@loot-os/ui/components/card";
import { Link } from "@tanstack/react-router";

import { useCurrency } from "../../lib/currency.js";
import { seatLabel } from "../../lib/seatLabel.js";
import { useTerminology } from "../../lib/terminology.js";
import { SeatProfileTrigger } from "./SeatName.js";

/**
 * The completed stage's banner: who won, and for a seated viewer the claim. The entry was paid
 * when the seat was taken, so nothing is charged here: a winner claims the items, a loser is
 * minted the battle's loser prize, when it carries one, and either can be sold straight back
 * instead — the claim, then every minted unit sold to the house for its value, in one call.
 */
export function BattleCompletedPanel({
  battle,
  viewerEndUserId,
  claiming,
  claimError,
  onClaim,
  sellingBack,
  sellBackError,
  onSellBack,
  onReplay,
}: {
  battle: Battle;
  viewerEndUserId: string | null | undefined;
  claiming: boolean;
  claimError: string | null;
  onClaim: () => void;
  sellingBack: boolean;
  sellBackError: string | null;
  onSellBack: () => void;
  onReplay: () => void;
}) {
  const currency = useCurrency();
  const { item, inventory, battle: battleTerm } = useTerminology();
  const winner = battle.winnerSeatIndex !== null ? battle.seats[battle.winnerSeatIndex] : undefined;
  const me = battle.me;
  const iWon = me !== null && me.seatIndex === battle.winnerSeatIndex;
  const wonValue = winner?.totalValueCents ?? null;
  const potCents = battle.seats.reduce((sum, seat) => sum + (seat.totalValueCents ?? 0), 0);
  const prize = me !== null && !iWon ? battle.loserPrizeItem : null;
  // What a sell-back credits this seat: the pot for the winner, the prize for a loser.
  const sellBackCents = iWon ? potCents : (prize?.value ?? 0);
  const busy = claiming || sellingBack;
  const error = claimError ?? sellBackError;
  // A loser on a battle without a prize has nothing to mint; the claim just closes the seat.
  const claimLabel = iWon ? `Claim ${item.plural}` : prize ? `Claim ${prize.name}` : "Done";

  return (
    <Card>
      <CardContent className="grid gap-4 p-5">
        <div className="text-center">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            {battle.mode === "crazy" ? "Lowest total takes it all" : "Highest total takes it all"}
          </p>
          <h2 className="mt-1 text-2xl font-semibold tracking-tight">
            {winner ? (
              <>
                <SeatProfileTrigger
                  seat={winner}
                  viewerEndUserId={viewerEndUserId}
                  className="hover:underline"
                >
                  {seatLabel(winner, viewerEndUserId)}
                </SeatProfileTrigger>{" "}
                {iWon ? "win" : "wins"}
              </>
            ) : (
              "Finished"
            )}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {wonValue !== null ? `${formatAmountWithUnit(wonValue, currency)} pulled · ` : ""}
            every item worth {formatAmountWithUnit(potCents, currency)} goes to the winner
          </p>
        </div>

        {me ? (
          <div className="grid justify-items-center gap-3 border-t border-border pt-4 text-center">
            {prize ? (
              <div className="flex items-center gap-3 rounded-md border border-border bg-muted/40 px-3 py-2 text-left text-sm">
                <BoxImage
                  src={prize.imageUrl}
                  className="size-12 shrink-0 rounded object-cover"
                  fallback={<span className="text-xl opacity-40">🎁</span>}
                />
                <div>
                  <p className="font-medium">{prize.name}</p>
                  <p className="text-xs text-muted-foreground">
                    Your prize for playing, worth {formatAmountWithUnit(prize.value, currency)}
                  </p>
                </div>
              </div>
            ) : null}
            {me.claimStatus === "claimed" ? (
              <p className="text-sm text-emerald-500">
                {me.soldCents !== null
                  ? `Sold back for ${formatAmountWithUnit(me.soldCents, currency)}.`
                  : iWon
                    ? `Claimed — the ${item.plural} are in your ${inventory.singular}.`
                    : prize
                      ? `Claimed — the ${prize.name} is in your ${inventory.singular}.`
                      : "Done — nothing to claim on this one."}
              </p>
            ) : (
              <>
                <div className="flex flex-wrap justify-center gap-2">
                  <Button size="lg" disabled={busy} onClick={onClaim}>
                    {claiming ? "Claiming…" : claimLabel}
                  </Button>
                  {sellBackCents > 0 ? (
                    <Button size="lg" variant="outline" disabled={busy} onClick={onSellBack}>
                      {sellingBack
                        ? "Selling back…"
                        : `Sell back · ${formatAmountWithUnit(sellBackCents, currency)}`}
                    </Button>
                  ) : null}
                </div>
                <p className="max-w-md text-xs text-muted-foreground">
                  Your entry of {formatAmountWithUnit(me.costCents, currency)} was paid when you
                  took your seat; nothing more is charged.
                  {iWon
                    ? ` Claim the ${item.plural} to add them to your ${inventory.singular}`
                    : prize
                      ? ` Claim the ${prize.name} to add it to your ${inventory.singular}`
                      : " There is nothing to claim"}
                  {sellBackCents > 0
                    ? `, or sell ${iWon ? "them" : "it"} straight back for ${formatAmountWithUnit(sellBackCents, currency)}`
                    : ""}
                  . You can't open or join anything else until you decide.
                </p>
              </>
            )}
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
          </div>
        ) : null}

        <div className="flex flex-wrap justify-center gap-2 border-t border-border pt-4">
          <Button variant="secondary" size="sm" onClick={onReplay}>
            Watch replay
          </Button>
          <Link to="/battles" className={buttonVariants({ variant: "ghost", size: "sm" })}>
            Back to {battleTerm.plural}
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
